/**
 * Priority Settings Form Component
 *
 * Allows merchants to configure priority levels (High/Normal/Ignore) for each
 * content type (products, collections, pages, articles). Priority determines how
 * content changes are processed with respect to Google's limited daily quota.
 *
 * Priority Levels:
 * - High: Google + IndexNow, prioritized in queue (uses quota first)
 * - Normal: Google + IndexNow, standard queue (uses remaining quota)
 * - Ignore: IndexNow only, lowest priority (never uses Google quota)
 *
 * @see docs/PRD.md for priority settings requirements
 * @see app/src/pages/types/api.ts for MerchantSettings type
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Card,
  FormLayout,
  Select,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Box,
  SkeletonBodyText,
} from '@shopify/polaris';
import type { ContentPriority, MerchantSettings, ApiResult } from '../types/api';
import { fetchSettings, updateSettings } from '../lib/api-client';

// =============================================================================
// Types
// =============================================================================

export interface PrioritySettingsFormProps {
  /** Callback when settings are saved successfully */
  onSaveSuccess?: () => void;
  /** Show toast message */
  onShowToast?: (message: string, isError?: boolean) => void;
}

interface PriorityFormState {
  priorityNewProducts: ContentPriority;
  priorityProductUpdates: ContentPriority;
  priorityPriceChanges: ContentPriority;
  priorityNewCollections: ContentPriority;
  priorityCollectionUpdates: ContentPriority;
  priorityBlogPosts: ContentPriority;
  priorityPages: ContentPriority;
}

// =============================================================================
// Constants
// =============================================================================

/** Priority options for Select components */
const PRIORITY_OPTIONS = [
  {
    label: 'High',
    value: 'high' as ContentPriority,
  },
  {
    label: 'Normal',
    value: 'normal' as ContentPriority,
  },
  {
    label: 'Ignore',
    value: 'ignore' as ContentPriority,
  },
];

/** Default priority settings (matching PRD defaults) */
const DEFAULT_PRIORITIES: PriorityFormState = {
  priorityNewProducts: 'high',
  priorityProductUpdates: 'normal',
  priorityPriceChanges: 'ignore',
  priorityNewCollections: 'high',
  priorityCollectionUpdates: 'normal',
  priorityBlogPosts: 'normal',
  priorityPages: 'normal',
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Extract priority settings from full merchant settings
 */
function extractPriorityState(settings: MerchantSettings): PriorityFormState {
  return {
    priorityNewProducts: settings.priorityNewProducts,
    priorityProductUpdates: settings.priorityProductUpdates,
    priorityPriceChanges: settings.priorityPriceChanges,
    priorityNewCollections: settings.priorityNewCollections,
    priorityCollectionUpdates: settings.priorityCollectionUpdates,
    priorityBlogPosts: settings.priorityBlogPosts,
    priorityPages: settings.priorityPages,
  };
}

/**
 * Check if two priority states are equal
 */
function arePrioritiesEqual(a: PriorityFormState, b: PriorityFormState): boolean {
  return (
    a.priorityNewProducts === b.priorityNewProducts &&
    a.priorityProductUpdates === b.priorityProductUpdates &&
    a.priorityPriceChanges === b.priorityPriceChanges &&
    a.priorityNewCollections === b.priorityNewCollections &&
    a.priorityCollectionUpdates === b.priorityCollectionUpdates &&
    a.priorityBlogPosts === b.priorityBlogPosts &&
    a.priorityPages === b.priorityPages
  );
}

// =============================================================================
// Skeleton Component
// =============================================================================

/**
 * Loading skeleton for PrioritySettingsForm
 */
function PrioritySettingsFormSkeleton() {
  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          Content Priority Settings
        </Text>
        <SkeletonBodyText lines={8} />
      </BlockStack>
    </Card>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Form for configuring priority settings for each content type.
 *
 * Features:
 * - Select dropdowns for Products, Collections, Pages, and Articles
 * - Each has three options: High, Normal, Ignore
 * - Help text explaining what each priority level means
 * - Form state tracks unsaved changes
 * - Save button enabled only when changes exist
 * - Shows success toast on save
 * - Handles save errors with error banner
 *
 * @example
 * ```tsx
 * <PrioritySettingsForm
 *   onSaveSuccess={() => console.log('Settings saved!')}
 *   onShowToast={(msg) => toast.show(msg)}
 * />
 * ```
 */
export function PrioritySettingsForm({
  onSaveSuccess,
  onShowToast,
}: PrioritySettingsFormProps) {
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Form state
  const [formState, setFormState] = useState<PriorityFormState>(DEFAULT_PRIORITIES);
  const [savedState, setSavedState] = useState<PriorityFormState>(DEFAULT_PRIORITIES);

  // Calculate if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return !arePrioritiesEqual(formState, savedState);
  }, [formState, savedState]);

  // Fetch settings on mount
  useEffect(() => {
    async function loadSettings() {
      setLoading(true);
      setFetchError(null);

      const result: ApiResult<MerchantSettings> = await fetchSettings();

      if (result.success) {
        const priorities = extractPriorityState(result.data);
        setFormState(priorities);
        setSavedState(priorities);
      } else {
        setFetchError(result.error.error);
      }

      setLoading(false);
    }

    loadSettings();
  }, []);

  // Handle field change
  const handleChange = useCallback(
    (field: keyof PriorityFormState) => (value: string) => {
      setFormState((prev) => ({
        ...prev,
        [field]: value as ContentPriority,
      }));
      // Clear save error when user makes changes
      setSaveError(null);
    },
    []
  );

  // Handle save
  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    const result: ApiResult<void> = await updateSettings(formState);

    if (result.success) {
      setSavedState(formState);
      onShowToast?.('Priority settings saved');
      onSaveSuccess?.();
    } else {
      setSaveError(result.error.error);
      onShowToast?.(result.error.error, true);
    }

    setSaving(false);
  }, [formState, onSaveSuccess, onShowToast]);

  // Handle discard changes
  const handleDiscard = useCallback(() => {
    setFormState(savedState);
    setSaveError(null);
  }, [savedState]);

  // Loading state
  if (loading) {
    return <PrioritySettingsFormSkeleton />;
  }

  // Fetch error state
  if (fetchError) {
    return (
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            Content Priority Settings
          </Text>
          <Banner tone="critical">
            Failed to load settings: {fetchError}
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        <Text as="h2" variant="headingMd">
          Content Priority Settings
        </Text>

        {/* Help text explaining priorities */}
        <Banner tone="info">
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              How priorities affect indexing:
            </Text>
            <BlockStack gap="100">
              <Text as="p" variant="bodyMd">
                <strong>High:</strong> Google + IndexNow, prioritized in queue. Uses Google quota first.
              </Text>
              <Text as="p" variant="bodyMd">
                <strong>Normal:</strong> Google + IndexNow, standard queue. Uses remaining Google quota.
              </Text>
              <Text as="p" variant="bodyMd">
                <strong>Ignore:</strong> IndexNow only, lowest priority. Never uses Google quota.
              </Text>
            </BlockStack>
          </BlockStack>
        </Banner>

        {/* Save error banner */}
        {saveError && (
          <Banner tone="critical" onDismiss={() => setSaveError(null)}>
            {saveError}
          </Banner>
        )}

        {/* Form */}
        <FormLayout>
          {/* Products Section */}
          <FormLayout.Group>
            <Select
              label="New products"
              options={PRIORITY_OPTIONS}
              value={formState.priorityNewProducts}
              onChange={handleChange('priorityNewProducts')}
              helpText="When a new product is created"
            />
            <Select
              label="Product updates"
              options={PRIORITY_OPTIONS}
              value={formState.priorityProductUpdates}
              onChange={handleChange('priorityProductUpdates')}
              helpText="When product details are updated"
            />
          </FormLayout.Group>

          <Select
            label="Price-only changes"
            options={PRIORITY_OPTIONS}
            value={formState.priorityPriceChanges}
            onChange={handleChange('priorityPriceChanges')}
            helpText="When only the price changes (no other updates)"
          />

          {/* Collections Section */}
          <FormLayout.Group>
            <Select
              label="New collections"
              options={PRIORITY_OPTIONS}
              value={formState.priorityNewCollections}
              onChange={handleChange('priorityNewCollections')}
              helpText="When a new collection is created"
            />
            <Select
              label="Collection updates"
              options={PRIORITY_OPTIONS}
              value={formState.priorityCollectionUpdates}
              onChange={handleChange('priorityCollectionUpdates')}
              helpText="When collection details are updated"
            />
          </FormLayout.Group>

          {/* Blog Posts and Pages */}
          <FormLayout.Group>
            <Select
              label="Blog posts (articles)"
              options={PRIORITY_OPTIONS}
              value={formState.priorityBlogPosts}
              onChange={handleChange('priorityBlogPosts')}
              helpText="When blog articles are created or updated"
            />
            <Select
              label="Pages"
              options={PRIORITY_OPTIONS}
              value={formState.priorityPages}
              onChange={handleChange('priorityPages')}
              helpText="When custom pages are created or updated"
            />
          </FormLayout.Group>
        </FormLayout>

        {/* Action buttons */}
        <Box paddingBlockStart="200">
          <InlineStack gap="300" align="end">
            {hasUnsavedChanges && (
              <Button onClick={handleDiscard} disabled={saving}>
                Discard
              </Button>
            )}
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!hasUnsavedChanges}
            >
              Save
            </Button>
          </InlineStack>
        </Box>
      </BlockStack>
    </Card>
  );
}
